import { useModal } from "@ebay/nice-modal-react";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldClaimMapping, {
  ClaimMappingFormValue,
  EMPTY_CLAIM_MAPPING_VALUE,
} from "components/FormFields/ClaimMapping";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import useTypedContext from "hooks/useTypedContext";
import { OidcSettings } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import DrawerSection from "ds/components/Drawer/Section";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";

import { UPDATE_OIDC_SSO } from "./gql";

const useUpdateOIDC = createDefaultMutationHook(UPDATE_OIDC_SSO);

type SettingsSingleSignOnEditOIDCFormFields = {
  claimMapping: ClaimMappingFormValue[];
};

type SettingsSingleSignOnEditOIDCProps = {
  integration: OidcSettings;
  setIsDirty: (isDirty: boolean) => void;
};

const SettingsSingleSignOnEditOIDC = ({
  integration,
  setIsDirty,
}: SettingsSingleSignOnEditOIDCProps) => {
  const modal = useModal();
  const { reportSuccess } = useTypedContext(FlashContext);

  const [oidcUpdate, { loading }] = useUpdateOIDC({ refetchQueries: ["GetSSOSettings"] });

  const form = useForm<SettingsSingleSignOnEditOIDCFormFields>({
    mode: "onChange",
    defaultValues: {
      claimMapping: integration.claimMapping.entries.length
        ? integration.claimMapping.entries
        : [EMPTY_CLAIM_MAPPING_VALUE],
    },
  });

  const {
    formState: { isDirty },
    handleSubmit,
  } = form;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationOIDC,
    defaultCallbackTrackProperties: {
      method: "OIDC",
    },
  });

  const onSubmit = handleSubmit((data) => {
    trackSegmentAnalyticsEvent("Edit Save");

    const filteredClaimMapping = data.claimMapping.filter(
      (claim) => claim.name.trim() && claim.value.trim()
    );

    oidcUpdate(
      {
        input: {
          claimMapping: { entries: filteredClaimMapping },
        },
      },
      (data) => {
        if (data?.oidcUpdate) {
          reportSuccess({ message: "OIDC settings updated" });
          modal.hide();
        }
      }
    );
  });

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} {...getDrawerFormFix()}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Edit OIDC SSO" />
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody noPadding>
          <DrawerSection gap="x-large">
            <FormFieldClaimMapping />
          </DrawerSection>
        </DrawerBody>

        <DrawerFooter fullWidth>
          <DrawerFooterActions>
            <DrawerCancelButton onClick={() => trackSegmentAnalyticsEvent("OIDC Edit Cancel")} />
            <Button variant="primary" type="submit" loading={loading}>
              Save
            </Button>
          </DrawerFooterActions>
        </DrawerFooter>
      </form>
    </FormProvider>
  );
};

export default SettingsSingleSignOnEditOIDC;
